/**
 * Iter 253v · Saved WhatsApp Templates manager.
 *
 * Admins create named templates that agents can pick from a dropdown in the
 * WA Inbox composer. Each saved template maps a friendly `name` +
 * `variables` prompt list to a raw Interakt template name.
 */
import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { apiErrorMessage } from "@/lib/apiError";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Save, X, Pencil, MessageCircle, ShieldCheck, RefreshCw, AlertTriangle, ClipboardPaste } from "lucide-react";

function TemplateForm({ initial, onSave, onCancel }) {
  const [f, setF] = useState(() => ({
    name: initial?.name || "",
    interakt_template_name: initial?.interakt_template_name || "",
    description: initial?.description || "",
    variables: initial?.variables || [],
    is_active: initial?.is_active !== false,
  }));
  const setVar = (i, key, val) => {
    const next = [...f.variables];
    next[i] = { ...next[i], [key]: val };
    setF({ ...f, variables: next });
  };
  const addVar = () => setF({ ...f, variables: [...f.variables, { label: "", placeholder: "" }] });
  const rmVar = (i) => setF({ ...f, variables: f.variables.filter((_, x) => x !== i) });
  const save = async () => {
    if (!f.name.trim() || !f.interakt_template_name.trim()) {
      toast.error("Name and Interakt template name are required");
      return;
    }
    // Strip incomplete variables.
    const cleaned = { ...f,
      variables: f.variables.filter((v) => (v.label || "").trim()) };
    await onSave(cleaned);
  };
  return (
    <div className="border border-primary bg-primary/5 p-3 space-y-2" data-testid="wa-tpl-form">
      <div className="grid grid-cols-2 gap-2">
        <div>
          <Label className="label-eyebrow mb-1 inline-block">Friendly name</Label>
          <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })}
                 placeholder="Weekly progress update" className="rounded-none"
                 data-testid="wa-tpl-name" />
        </div>
        <div>
          <Label className="label-eyebrow mb-1 inline-block">Interakt template name</Label>
          <Input value={f.interakt_template_name}
                 onChange={(e) => setF({ ...f, interakt_template_name: e.target.value })}
                 placeholder="weekly_progress_v1" className="rounded-none"
                 data-testid="wa-tpl-interakt" />
        </div>
      </div>
      <div>
        <Label className="label-eyebrow mb-1 inline-block">Description (optional)</Label>
        <Input value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })}
               placeholder="Short note for agents" className="rounded-none"
               data-testid="wa-tpl-desc" />
      </div>
      <div>
        <div className="flex items-center gap-2 mb-1">
          <Label className="label-eyebrow inline-block">Variables (order matters)</Label>
          <Button size="sm" variant="ghost" className="rounded-none h-6 px-2"
                  onClick={addVar} data-testid="wa-tpl-var-add">
            <Plus className="w-3 h-3 mr-1" /> Add variable
          </Button>
        </div>
        {f.variables.length === 0 && (
          <div className="text-[11px] text-muted-foreground italic">No variables — template renders as-is.</div>
        )}
        {f.variables.map((v, i) => (
          <div key={i} className="flex items-center gap-1.5 mb-1" data-testid={`wa-tpl-var-row-${i}`}>
            <span className="text-[10px] font-semibold text-muted-foreground w-8">{`{{${i + 1}}}`}</span>
            <Input value={v.label} placeholder="Variable label (e.g. Client name)"
                   onChange={(e) => setVar(i, "label", e.target.value)}
                   className="rounded-none h-8 flex-1"
                   data-testid={`wa-tpl-var-label-${i}`} />
            <Input value={v.placeholder} placeholder="Placeholder value"
                   onChange={(e) => setVar(i, "placeholder", e.target.value)}
                   className="rounded-none h-8 flex-1"
                   data-testid={`wa-tpl-var-placeholder-${i}`} />
            <button type="button" onClick={() => rmVar(i)}
                    className="p-1 hover:bg-rose-100 rounded-none"
                    data-testid={`wa-tpl-var-remove-${i}`}>
              <X className="w-3.5 h-3.5 text-rose-600" />
            </button>
          </div>
        ))}
      </div>
      <div className="flex justify-end gap-2 pt-1">
        <Button variant="ghost" size="sm" className="rounded-none" onClick={onCancel}
                data-testid="wa-tpl-cancel">Cancel</Button>
        <Button size="sm" className="rounded-none" onClick={save}
                data-testid="wa-tpl-save">
          <Save className="w-3.5 h-3.5 mr-1" /> Save template
        </Button>
      </div>
    </div>
  );
}

export default function WaTemplateLibrary() {
  const { activeWorkspaceId: wsId } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [editingId, setEditingId] = useState(null);

  const load = async () => {
    if (!wsId) return;
    setLoading(true);
    try {
      const r = await api.get(`/workspaces/${wsId}/wa/saved-templates`,
                                { params: { include_archived: true } });
      setRows(r.data);
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load templates")); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [wsId]);

  const create = async (payload) => {
    try {
      await api.post(`/workspaces/${wsId}/wa/saved-templates`, payload);
      toast.success("Template saved");
      setShowAdd(false); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Save failed")); }
  };
  const update = async (tid, payload) => {
    try {
      await api.patch(`/wa/saved-templates/${tid}`, payload);
      toast.success("Template updated");
      setEditingId(null); load();
    } catch (e) { toast.error(apiErrorMessage(e, "Update failed")); }
  };
  const destroy = async (tid, name) => {
    if (!window.confirm(`Delete template "${name}"?`)) return;
    try {
      await api.delete(`/wa/saved-templates/${tid}`);
      toast.success("Template deleted");
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Delete failed")); }
  };

  // Iter 253w · Cross-check every saved template against Interakt's live
  // approved-template list. Marks each row with an approval status badge so
  // agents know when a template silently lost its blessing.
  const [syncing, setSyncing] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);   // Feb 2026
  const syncApproval = async () => {
    setSyncing(true);
    try {
      const r = await api.post(`/workspaces/${wsId}/wa/saved-templates/sync-approval`);
      if (r.data.provider_error) {
        toast.warning(`Interakt: ${r.data.provider_error}`);
      } else {
        const parts = [
          `${r.data.approved || 0} approved`,
          `${r.data.pending || 0} pending`,
          `${r.data.rejected || 0} rejected`,
          `${r.data.not_found || 0} not found`,
        ];
        toast.success(`Synced ${r.data.checked} · ${parts.join(" · ")}`);
      }
      load();
    } catch (e) { toast.error(apiErrorMessage(e, "Sync failed")); }
    finally { setSyncing(false); }
  };

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="wa-tpl-library-page">
      <PageHeader
        title="WhatsApp Template Library"
        subtitle="Named Interakt templates with variable prompts — agents pick from a dropdown instead of typing raw template names." />
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => { setShowAdd(true); setEditingId(null); }}
                className="rounded-none" data-testid="wa-tpl-add-btn">
          <Plus className="w-3.5 h-3.5 mr-1" /> New template
        </Button>
        <Button size="sm" variant="outline" onClick={() => setShowBulkImport(true)}
                className="rounded-none" data-testid="wa-tpl-bulk-import-btn"
                title="Paste template URLs / names from Interakt dashboard to bulk-add">
          <ClipboardPaste className="w-3.5 h-3.5 mr-1" /> Bulk import from Interakt
        </Button>
        <Button size="sm" variant="outline" onClick={syncApproval} disabled={syncing}
                className="rounded-none" data-testid="wa-tpl-sync-btn">
          <RefreshCw className={`w-3.5 h-3.5 mr-1 ${syncing ? "animate-spin" : ""}`} />
          {syncing ? "Syncing…" : "Sync approval"}
        </Button>
        <span className="text-xs text-muted-foreground ml-auto">
          {rows.filter((r) => r.is_active !== false).length} active · {rows.filter((r) => r.is_active === false).length} archived
        </span>
      </div>

      {showBulkImport && (
        <BulkImportDialog wsId={wsId}
                           onClose={() => setShowBulkImport(false)}
                           onDone={() => { setShowBulkImport(false); load(); }} />
      )}

      {showAdd && (
        <TemplateForm initial={null} onSave={create}
                       onCancel={() => setShowAdd(false)} />
      )}

      <div className="border border-border divide-y divide-border">
        {loading && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
        {!loading && rows.length === 0 && (
          <div className="p-6 text-center text-sm text-muted-foreground" data-testid="wa-tpl-empty">
            <MessageCircle className="w-8 h-8 mx-auto mb-2 opacity-40" />
            No saved templates yet. Add your first to give agents a quick-send dropdown.
          </div>
        )}
        {rows.map((r) => (
          editingId === r.id ? (
            <div key={r.id} className="p-2 bg-muted/20">
              <TemplateForm initial={r}
                             onSave={(payload) => update(r.id, payload)}
                             onCancel={() => setEditingId(null)} />
            </div>
          ) : (
            <div key={r.id} className={`p-3 flex items-start gap-3 ${r.is_active === false ? "opacity-50" : ""}`}
                 data-testid={`wa-tpl-row-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-sm" data-testid={`wa-tpl-name-${r.id}`}>{r.name}</span>
                  <span className="text-[10px] text-muted-foreground font-mono bg-muted px-1.5 py-0.5">
                    {r.interakt_template_name}
                  </span>
                  {/* Iter 253w · Approval status badge (populated by sync-approval). */}
                  {r.interakt_approval_status && (
                    (() => {
                      const s = r.interakt_approval_status;
                      const cfg = s === "approved" ? { cls: "bg-emerald-100 text-emerald-800 border-emerald-300", Icon: ShieldCheck, label: "Approved" }
                        : s === "pending" ? { cls: "bg-amber-50 text-amber-800 border-amber-300", Icon: RefreshCw, label: "Pending" }
                        : s === "rejected" ? { cls: "bg-rose-100 text-rose-800 border-rose-300", Icon: AlertTriangle, label: "Rejected" }
                        : s === "not_found" ? { cls: "bg-rose-50 text-rose-700 border-rose-300", Icon: AlertTriangle, label: "Not on Interakt" }
                        : { cls: "bg-slate-50 text-slate-500 border-slate-200", Icon: AlertTriangle, label: s };
                      const Icon = cfg.Icon;
                      return (
                        <span className={`inline-flex items-center gap-1 text-[9px] px-1.5 py-0.5 border rounded uppercase tracking-wider ${cfg.cls}`}
                                data-testid={`wa-tpl-status-${r.id}`}
                                title={r.interakt_last_synced_at ? `Synced ${new Date(r.interakt_last_synced_at).toLocaleString()}` : cfg.label}>
                          <Icon className="w-2.5 h-2.5" /> {cfg.label}
                        </span>
                      );
                    })()
                  )}
                  {r.is_active === false && <span className="chip text-[10px] bg-muted text-muted-foreground">Archived</span>}
                </div>
                {r.description && <div className="text-xs text-muted-foreground mt-0.5">{r.description}</div>}
                {r.variables?.length > 0 && (
                  <div className="mt-1.5 flex flex-wrap gap-1">
                    {r.variables.map((v, i) => (
                      <span key={i} className="text-[10px] border border-border px-1.5 py-0.5 bg-background">
                        <span className="text-muted-foreground font-mono">{`{{${i + 1}}}`}</span> {v.label}
                      </span>
                    ))}
                  </div>
                )}
              </div>
              <div className="flex items-center gap-1">
                <button type="button" onClick={() => { setEditingId(r.id); setShowAdd(false); }}
                        className="p-1 hover:bg-muted"
                        data-testid={`wa-tpl-edit-${r.id}`} title="Edit">
                  <Pencil className="w-3.5 h-3.5" />
                </button>
                <button type="button" onClick={() => destroy(r.id, r.name)}
                        className="p-1 hover:bg-rose-100 text-rose-600"
                        data-testid={`wa-tpl-delete-${r.id}`} title="Delete">
                  <Trash2 className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )
        ))}
      </div>
    </div>
  );
}

// ─── Feb 2026 · Bulk import from Interakt Dashboard paste ─────

function BulkImportDialog({ wsId, onClose, onDone }) {
  const [text, setText] = useState("");
  const [category, setCategory] = useState("MARKETING");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);

  const submit = async () => {
    if (!text.trim()) { toast.error("Paste at least one template URL or name"); return; }
    setBusy(true); setResult(null);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/wa/saved-templates/bulk-import`, {
        text, category,
      });
      setResult(data);
      if (data.added > 0) toast.success(`Added ${data.added} template${data.added !== 1 ? "s" : ""}`);
    } catch (e) { toast.error(apiErrorMessage(e, "Bulk import failed")); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={onClose}>
      <div className="bg-background border border-border w-full max-w-2xl p-4 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="wa-tpl-bulk-import-dialog">
        <div className="font-bold text-sm inline-flex items-center gap-2">
          <ClipboardPaste className="w-4 h-4 text-primary" /> Bulk import templates from Interakt
        </div>
        <div className="text-xs text-muted-foreground space-y-1">
          <p><b>Step 1</b>: Open <a href="https://app.interakt.ai/templates/list" target="_blank" rel="noreferrer" className="underline text-primary">app.interakt.ai/templates/list</a> in a new tab.</p>
          <p><b>Step 2</b>: For each approved template, right-click → <i>Copy link</i> and paste below (one URL per line). You can also paste plain template code names.</p>
          <p><b>Step 3</b>: We&apos;ll auto-extract the template code-names and add them to your library. Existing ones are skipped.</p>
        </div>
        <div>
          <Label className="text-xs font-semibold">Paste URLs or names</Label>
          <textarea rows={8} value={text} onChange={(e) => setText(e.target.value)}
                     placeholder={"https://app.interakt.ai/templates/list/template/aquarius_welcome/view\nhttps://app.interakt.ai/templates/list/template/aquarius_promo/view\naquarius_reminder"}
                     className="w-full border border-border bg-background px-2 py-1.5 text-xs rounded-none font-mono"
                     data-testid="wa-tpl-bulk-text" />
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label className="text-xs font-semibold">Default category</Label>
            <select value={category} onChange={(e) => setCategory(e.target.value)}
                     className="w-full h-9 border border-border bg-background px-2 text-sm rounded-none"
                     data-testid="wa-tpl-bulk-category">
              <option value="MARKETING">Marketing</option>
              <option value="UTILITY">Utility</option>
              <option value="AUTHENTICATION">Authentication</option>
            </select>
          </div>
        </div>

        {result && (
          <div className="border border-border p-2 max-h-40 overflow-y-auto" data-testid="wa-tpl-bulk-result">
            <div className="text-xs font-semibold mb-1">
              Result: {result.added} added · {result.duplicate} already existed · {result.total_candidates} total
            </div>
            <ul className="text-[11px] space-y-0.5">
              {(result.results || []).map((r) => (
                <li key={r.name} className={r.status === "added" ? "text-emerald-700" : "text-muted-foreground"}>
                  {r.status === "added" ? "✓" : "·"} {r.name} ({r.status})
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex justify-end gap-2 pt-2 border-t border-border">
          <button className="text-xs px-3 py-1.5 border border-border hover:bg-muted" onClick={onDone || onClose}>
            {result && result.added > 0 ? "Done" : "Cancel"}
          </button>
          <button className="text-xs px-3 py-1.5 bg-primary text-primary-foreground disabled:opacity-50"
                   onClick={submit} disabled={busy || !text.trim()}
                   data-testid="wa-tpl-bulk-submit">
            {busy ? "Importing…" : "Extract & add"}
          </button>
        </div>
      </div>
    </div>
  );
}

